import { Button, Typography } from '@scale/scaleui';
import { fieldNameToDisplayNameMapping } from './SignupFlowFastLaneQualificationCondition';
import {
  StringOperator,
  NumberOperator,
  IExtendedSignupFlow,
  SignupFlowSyncCredentialConditionGroup,
  getResumeScreeningConfigIdForConditionalFlow,
  SignupFlowConditionGroupType,
} from '@scale/shared-types';
import { useMemo } from 'react';
import { Icon, IconButton, SegmentedControl, Tooltip } from '@scale/scaleui-radix';
import { faExternalLink } from '@fortawesome/pro-regular-svg-icons';

const operatorToDisplayNameMapping = {
  [StringOperator.Equals]: 'is',
  [StringOperator.OneOf]: 'in',
  [StringOperator.InList]: 'in',
  [NumberOperator.Min]: '>=',
  [NumberOperator.Max]: '<=',
};

const MAX_CONDITION_GROUPS_TO_DISPLAY = 1;
const MAX_CONDITIONS_TO_DISPLAY = 5;
const MAX_VALUES_TO_DISPLAY = 5;

interface SignupFlowConditionSummaryProps {
  signupFlowConfig: Partial<IExtendedSignupFlow>;
  setSignupFlowConfig: (signupFlowConfig: Partial<IExtendedSignupFlow>) => void;
  setIsQualificationModalOpen: (isOpen: boolean) => void;
}

export const SignupFlowConditionSummary = ({
  signupFlowConfig,
  setSignupFlowConfig,
  setIsQualificationModalOpen,
}: SignupFlowConditionSummaryProps) => {
  const removeConditions = () => {
    setSignupFlowConfig({
      ...signupFlowConfig,
      conditionalConfig: undefined,
    });
  };

  const allSyncConditions =
    (signupFlowConfig.conditionalConfig?.conditions || [])
      .filter(group => group.type === SignupFlowConditionGroupType.Sync)
      .map(group => group as SignupFlowSyncCredentialConditionGroup)
      .map(group =>
        group.conditions.map(condition => ({
          field: condition.field,
          operator: condition.operator,
          value: condition.value,
        })),
      ) || [];

  // Only display the first few conditions and values for the first group
  const syncConditionsToDisplay = allSyncConditions
    .slice(0, MAX_CONDITION_GROUPS_TO_DISPLAY)
    .map(group =>
      group.slice(0, MAX_CONDITIONS_TO_DISPLAY).map(condition => ({
        field: condition.field,
        operator: condition.operator,
        value: Array.isArray(condition.value)
          ? condition.value.slice(0, MAX_VALUES_TO_DISPLAY)
          : [condition.value],
      })),
    );

  const hasMoreConditions =
    allSyncConditions.length > 1 ||
    (allSyncConditions[0] && allSyncConditions[0].length > 3) ||
    allSyncConditions[0]?.some(
      condition => Array.isArray(condition.value) && condition.value.length > MAX_VALUES_TO_DISPLAY,
    );

  const resumeScreeningConfigId = useMemo<string | null>(() => {
    return getResumeScreeningConfigIdForConditionalFlow(signupFlowConfig.conditionalConfig);
  }, [signupFlowConfig]);

  const hasConditions = syncConditionsToDisplay.length > 0 || resumeScreeningConfigId !== null;

  const onOperatorChange = (value: 'and' | 'or') => {
    setSignupFlowConfig({
      ...signupFlowConfig,
      conditionalConfig: {
        conditions: signupFlowConfig.conditionalConfig?.conditions ?? [],
        operator: value,
      },
    });
  };

  const operatorTooltipContent = useMemo(() => {
    return signupFlowConfig.conditionalConfig?.operator === 'or'
      ? 'Contributors must pass either the sync conditions or the LLM screening to qualify'
      : 'Contributors must pass the sync conditions and the LLM screening to qualify';
  }, [signupFlowConfig.conditionalConfig?.operator]);

  return (
    <div className="w-full gap-x-3 items-center">
      <div className="flex justify-between gap-x-2 items-center">
        <Typography.Paragraph3>Conditions:</Typography.Paragraph3>
        <div className="flex gap-x-2">
          <Button onClick={() => setIsQualificationModalOpen(true)} variant="secondary" size="sm">
            {hasConditions ? 'Edit' : 'Add'}
          </Button>
          {hasConditions && (
            <Button onClick={removeConditions} variant="neutral" size="sm">
              Remove
            </Button>
          )}
        </div>
      </div>
      <>
        <div className="mt-2">
          {!hasConditions ? (
            <Typography.Paragraph3>No conditions</Typography.Paragraph3>
          ) : (
            <>
              {syncConditionsToDisplay.map((conditionGroup, groupIndex) => (
                <div key={groupIndex}>
                  {conditionGroup.map((condition, conditionIndex) => (
                    <div key={conditionIndex} className="flex flex-row gap-x-1">
                      <Typography.Paragraph3>
                        {fieldNameToDisplayNameMapping[condition.field]}
                      </Typography.Paragraph3>
                      <Typography.Paragraph3>
                        {operatorToDisplayNameMapping[condition.operator]}
                      </Typography.Paragraph3>
                      <Typography.Paragraph3>
                        {condition.value?.length > 0
                          ? condition.value
                              .map(value =>
                                value.toString()?.length ? value.toString() : '<No value>',
                              )
                              .join(', ')
                          : '<No value>'}
                        {Array.isArray(allSyncConditions[groupIndex][conditionIndex].value) &&
                          (allSyncConditions[groupIndex][conditionIndex].value as string[]).length >
                            MAX_VALUES_TO_DISPLAY &&
                          ' ...'}
                      </Typography.Paragraph3>
                    </div>
                  ))}
                </div>
              ))}

              {resumeScreeningConfigId && (
                <div className="flex items-center gap-x-2 mt-2">
                  {/* Show operator select if both condition types are present */}
                  {!!syncConditionsToDisplay.length && (
                    // Add a tooltip around segmented contro to describe what it does
                    <Tooltip content={operatorTooltipContent}>
                      <SegmentedControl.Root
                        value={signupFlowConfig.conditionalConfig?.operator ?? 'and'}
                        size="1"
                        onValueChange={(value: string) => onOperatorChange(value as 'and' | 'or')}
                      >
                        <SegmentedControl.Item value="or">or</SegmentedControl.Item>
                        <SegmentedControl.Item value="and">and</SegmentedControl.Item>
                      </SegmentedControl.Root>
                    </Tooltip>
                  )}
                  <Typography.Paragraph3>LLM Resume Screening</Typography.Paragraph3>
                  <IconButton
                    variant="soft"
                    onClick={() =>
                      window.open(
                        `/corp/growth-center/resume-screening/${resumeScreeningConfigId}`,
                        '_blank',
                      )
                    }
                  >
                    <Icon label="Open Resume Screening" icon={faExternalLink} size="2" />
                  </IconButton>
                </div>
              )}
            </>
          )}
        </div>
        {hasMoreConditions && (
          <div onClick={() => setIsQualificationModalOpen(true)}>
            <Typography.Paragraph3 className="text-neutral-500 underline cursor-pointer">
              View all conditions
            </Typography.Paragraph3>
          </div>
        )}
      </>
    </div>
  );
};
